/**
 * single_input 字段类型（value_type）— JS 端类型语义契约。
 *
 * §3 类型语义契约表是 Python 与 JS 两侧唯一真相源，本文件是 JS 侧实现。
 *
 * 约定：
 * - 空值（null / ""）按 §3 表输出：str → ""，其余 → null
 * - isValid 只判类型，不抛异常
 * - parseInput 强制转换，失败抛 Error（用于切换类型 / 提交时校验）
 * - convertBetween 在类型间尽量无损转换，转不动则保留原文并标 valid=false
 * - bool 不被 int / float 校验误收
 * - json / list 宽容解析：JSON.parse 失败后做一次宽松规范化再试一次；
 *   json 接受对象或数组，list 仅接受数组。
 */

export const VALUE_TYPES = ["str", "int", "float", "bool", "json", "list"];

/**
 * §3 「空值输出」列语义。
 * @param {*} value
 * @param {string} valueType
 * @returns {boolean}
 */
export function isEmpty(value, valueType) {
  if (value === null || value === undefined) return true;
  // 字符串的空白输入视为空（前端控件未填）
  if (typeof value === "string" && value.trim() === "") return true;
  if (valueType === "str") return value === "";
  // int / float / bool：0 / false / 0.0 不是空
  return false;
}

/**
 * 判断 value 是否满足 valueType 声明的合法形态，不抛异常。
 * @param {*} value
 * @param {string} valueType
 * @returns {boolean}
 */
export function isValid(value, valueType) {
  if (!VALUE_TYPES.includes(valueType)) return false;

  switch (valueType) {
    case "str":
      return typeof value === "string";
    case "int":
      // 必须先排除 bool
      if (typeof value === "boolean") return false;
      // JS 运行时 3 与 3.0 都是 Number（无法区分），均视为合法整值；
      // 文本层 "3.0" 的严格区分由 parseInput 用正则保证。
      return typeof value === "number" && Number.isInteger(value);
    case "float":
      if (typeof value === "boolean") return false;
      return typeof value === "number" && Number.isFinite(value);
    case "bool":
      return typeof value === "boolean";
    case "json":
      return typeof value === "object" && value !== null && !Array.isArray(value)
        ? true
        : Array.isArray(value);
    case "list":
      // 仅数组；与 json 区别：拒绝 dict
      return Array.isArray(value);
    default:
      return false;
  }
}

/**
 * 文本 → 原生值。失败抛 Error。
 * @param {string} text
 * @param {string} valueType
 * @returns {*}
 */
export function parseInput(text, valueType) {
  if (!VALUE_TYPES.includes(valueType)) {
    throw new Error(`unknown value_type: ${valueType}`);
  }

  // 空值统一处理
  if (isEmpty(text, valueType)) {
    return valueType === "str" ? "" : null;
  }

  switch (valueType) {
    case "str":
      return String(text);
    case "int": {
      const s = String(text).trim();
      if (!/^-?\d+$/.test(s)) {
        throw new Error(`无法将 ${JSON.stringify(text)} 解析为 int`);
      }
      return parseInt(s, 10);
    }
    case "float": {
      const s = String(text).trim();
      const n = Number(s);
      if (!Number.isFinite(n)) {
        throw new Error(`无法将 ${JSON.stringify(text)} 解析为 float`);
      }
      return n;
    }
    case "bool": {
      throw new Error(`bool 类型不接受文本输入 ${JSON.stringify(text)}`);
    }
    case "json":
      return _parseJson(text);
    case "list":
      return _parseList(text);
    default:
      throw new Error(`unreachable value_type: ${valueType}`);
  }
}

/**
 * 原生值 → 控件文本。
 * @param {*} value
 * @param {string} valueType
 * @returns {string}
 */
export function formatForInput(value, valueType) {
  if (isEmpty(value, valueType)) return "";
  switch (valueType) {
    case "str":
      return String(value);
    case "int":
    case "float":
      return String(value);
    case "bool":
      return value ? "true" : "false";
    case "json":
    case "list":
      return JSON.stringify(value);
    default:
      return "";
  }
}

/**
 * §6.3 切换类型时的旧值处理。
 *
 * 返回 {value, valid}：
 *   - valid=true：转换成功，value 是目标类型的原生值
 *   - valid=false：转换失败，value 是保留的原文（UI 显示红框）
 *
 * bool 下拉无非法态：str → bool 失败时置空（§6.3 表）。
 *
 * @param {*} value 原值（任意形态：可能是原生值也可能是字符串）
 * @param {string} fromType 原类型（"str" / "int" / "float" / "bool" / "json" / "list"）
 * @param {string} toType 目标类型
 * @returns {{value: *, valid: boolean}}
 */
export function convertBetween(value, fromType, toType) {
  if (!VALUE_TYPES.includes(toType)) {
    return { value, valid: false };
  }
  if (fromType === toType) {
    return { value, valid: isValid(value, toType) };
  }

  // 空值：直接按目标类型空值输出
  if (isEmpty(value, fromType)) {
    return { value: toType === "str" ? "" : null, valid: true };
  }

  // bool → 其它：原值即原生 bool，转字符串再按目标类型 parse
  if (fromType === "bool") {
    const text = value ? "true" : "false";
    return _convertText(text, "str", toType);
  }

  // 其它 → bool：bool 无非法态
  if (toType === "bool") {
    // 先尝试解析
    const text = formatForInput(value, fromType);
    const lc = String(text).toLowerCase();
    if (lc === "true" || lc === "1") return { value: true, valid: true };
    if (lc === "false" || lc === "0") return { value: false, valid: true };
    // §6.3 "abc" → bool：下拉无非法态，置空
    return { value: null, valid: true };
  }

  // 把 value 视为原生类型，转成 JSON 字符串（保持精度），再按目标类型 parse
  const text = formatForInput(value, fromType);
  return _convertText(text, "str", toType);
}

/**
 * 内部：text → toType，失败时保留 text 原文。
 */
function _convertText(text, fromType, toType) {
  if (toType === "str") return { value: String(text), valid: true };
  if (toType === "bool") {
    const lc = String(text).toLowerCase();
    if (lc === "true" || lc === "1") return { value: true, valid: true };
    if (lc === "false" || lc === "0") return { value: false, valid: true };
    return { value: null, valid: true };
  }
  try {
    const parsed = parseInput(text, toType);
    return { value: parsed, valid: true };
  } catch (_) {
    // §6.3 转换失败：保留原文
    return { value: text, valid: false };
  }
}

/**
 * §3.3 JS 端宽容 JSON 解析：先 JSON.parse，失败做一次宽松规范化再试一次。
 *
 * 仅做语法解析 + 宽松规范化，不约束结果类型。
 * 顶层类型约束由调用方（_parseJson / _parseList）检查。
 */
function _parseRelaxed(text) {
  const s = String(text).trim();
  // 第一轮：标准 JSON
  try {
    return JSON.parse(s);
  } catch (_) {
    // 第二轮：宽松规范化（单引号字符串 → 双引号、True/False/None 替换、去除尾随逗号）
    try {
      const relaxed = _relaxJson(s);
      return JSON.parse(relaxed);
    } catch (e) {
      throw new Error(`无法解析: ${e.message}`);
    }
  }
}

/**
 * §3.1 json 必须解析为对象或数组；标量视为非法。
 */
function _parseJson(text) {
  const parsed = _parseRelaxed(text);
  if (typeof parsed !== "object" || parsed === null) {
    if (!Array.isArray(parsed)) {
      throw new Error("json 仅接受对象或数组");
    }
  }
  return parsed;
}

/**
 * §3.1 list 必须解析为数组；dict / 标量视为非法。
 */
function _parseList(text) {
  const parsed = _parseRelaxed(text);
  if (!Array.isArray(parsed)) {
    throw new Error(`list 仅接受数组，得到 ${parsed === null ? "null" : typeof parsed}`);
  }
  return parsed;
}

/**
 * 文本级宽松规范化（不解析字符串内容，只做字符级替换）。
 * 已知限制：字符串内含撇号（如 {'msg': "it's ok"}）等极端情况可能规范化失败。
 */
function _relaxJson(text) {
  return text
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false")
    .replace(/\bNone\b/g, "null")
    // 把单引号包裹的字符串片段换成双引号
    .replace(/'((?:\\'|[^'])*)'/g, (_m, inner) => {
      // 把内部的双引号转义
      return '"' + inner.replace(/"/g, '\\"') + '"';
    })
    // 去除对象 / 数组字面量中的尾随逗号
    .replace(/,(\s*[}\]])/g, "$1");
}